import React, { useState, useEffect, useMemo, Suspense, lazy } from 'react';
import { Users, GraduationCap, BookOpen, Activity, UserPlus, CalendarPlus, Wallet, ArrowLeftRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import useAuthStore from '../store/authStore';
import { useSmartTranslation, useQuranCenter, useSportCenter, useLearningCenter } from '../hooks/useStudentLabel';
import useLearningPermissions from '../hooks/useLearningPermissions';
import useDashboardInsights from '../hooks/useDashboardInsights';
import { formatTime, formatPercent, toDate } from '../utils/localeFormatters';
import { useConfirm } from '../context/ConfirmContext';
import { useToastContext } from '../context/ToastContext';

import KPICard from '../components/Dashboard/KPICard';
import QuickActionsBar from '../components/Dashboard/QuickActionsBar';
import UpcomingLessonsList from '../components/Dashboard/UpcomingLessonsList';
import RecentActivityList from '../components/Dashboard/RecentActivityList';
import StudentSearchModal from '../components/Dashboard/StudentSearchModal';
import QuickAddStudentModal from '../components/Dashboard/QuickAddStudentModal';
import TodayTimeline from '../components/Dashboard/TodayTimeline';
import DashboardInsights from '../components/Dashboard/DashboardInsights';
import DashboardCharts from '../components/Dashboard/DashboardCharts';
import RegistrationSummaryCard from '../components/Dashboard/RegistrationSummaryCard';
import courseRegistrationApi from '../services/courseRegistrationApi';
import BirthdaysTodayChip from '../components/Sport/BirthdaysTodayChip';
import PendingIntakesCard from '../components/Sport/PendingIntakesCard';
import SportSessionsCard from '../components/Sport/SportSessionsCard';
import SportWeekCard from '../components/Sport/SportWeekCard';
import SportBalancesCard from '../components/Sport/SportBalancesCard';
import PrivateLessonsCard from '../components/Dashboard/PrivateLessonsCard';

import './LearningDashboardPage.css';

// Modals load on first open, not with the dashboard: every one of these
// sat in the main bundle (LessonModal alone is ~1100 lines), which pushed
// main.js past its 400KB gzip budget. FabRadial already lazy-loads its copies.
const LessonModal = lazy(() => import('../components/Dashboard/LessonModal'));
const PrivateLessonModal = lazy(() => import('../components/Dashboard/PrivateLessonModal'));
const RecordMemorizationModal = lazy(() => import('../components/Dashboard/RecordMemorizationModal'));
const AssignHomeworkModal = lazy(() => import('../components/Dashboard/AssignHomeworkModal'));
const CollectGroupsModal = lazy(() => import('../components/Dashboard/CollectGroupsModal'));
const QuickPaymentModal = lazy(() => import('../components/Dashboard/QuickPaymentModal'));
const ClientIntakeModal = lazy(() => import('../components/Sport/ClientIntakeModal'));
const SportScheduleModal = lazy(() => import('../components/Sport/SportScheduleModal'));
const SportWeekScheduleModal = lazy(() => import('../components/Sport/SportWeekScheduleModal'));
const SportClientBillingSheet = lazy(() => import('../components/Sport/SportClientBillingSheet'));
const MoveStudentsFlow = lazy(() => import('../components/Groups/MoveStudentsFlow'));

/**
 * Learning-center focused dashboard — lessons + students + teacher activity.
 *
 * DashboardPage.jsx delegates here when centerType === 'learning' so the
 * learning flow stays free of clinic/hair-transplant branching. Teachers
 * see a scoped view (teacherFilter=<uid>) via useLearningPermissions.
 */
export default function LearningDashboardPage({ teacherFilter = null, isTeacherView = false }) {
  const { i18n } = useTranslation();
  const t = useSmartTranslation();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const perms = useLearningPermissions();
  const { isQuran } = useQuranCenter();
  const { isSport } = useSportCenter();
  const { isLearning } = useLearningCenter();
  // Sport managers get intake + session scheduling as the dashboard's two
  // primary actions (docs/sport-center-plan.md §6). Teachers keep the
  // regular view — both flows are manager-only on the backend.
  const showSportHero = isSport && !isTeacherView && perms.canCreateLesson;
  // "Move students between groups" (owner request): managers only — the move
  // endpoint is manager-only. Sport shows it as a hero tile, every other
  // learning center in the quick-actions row.
  const canMoveStudents = !isTeacherView && !!perms.canCreateGroup && perms.role !== 'teacher';
  const confirm = useConfirm();
  const { success, error } = useToastContext();

  const effectiveTeacherFilter = isTeacherView
    ? teacherFilter || user?.uid
    : teacherFilter;

  const [students, setStudents] = useState([]);
  const [groups, setGroups] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [activities, setActivities] = useState([]);
  const [surveys, setSurveys] = useState([]);
  const [payments, setPayments] = useState([]);
  const [registrationGroups, setRegistrationGroups] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const [showLessonModal, setShowLessonModal] = useState(false);
  const [showStudentSearch, setShowStudentSearch] = useState(false);
  const [showRecord, setShowRecord] = useState(false);
  const [showAssignHw, setShowAssignHw] = useState(false);
  const [showCollect, setShowCollect] = useState(false);
  const [showQuickPayment, setShowQuickPayment] = useState(false);
  const [showQuickAddStudent, setShowQuickAddStudent] = useState(false);
  const [editingLesson, setEditingLesson] = useState(null);
  const [showIntake, setShowIntake] = useState(false);
  const [showSportSchedule, setShowSportSchedule] = useState(false);
  // Whole-week scheduler; holds the week to open on (null = closed).
  const [sportWeekOpen, setSportWeekOpen] = useState(null);
  const [showSportBilling, setShowSportBilling] = useState(false);
  const [showMoveFlow, setShowMoveFlow] = useState(false);
  const [sportRefresh, setSportRefresh] = useState(0);
  const [intakes, setIntakes] = useState([]);
  // Private 1-on-1 lessons live on the dashboard: schedule, take attendance
  // (which charges the student) and see teacher pay without leaving it.
  // Teaching centers only (no sub-type = teaching): sport bills per plan,
  // Quran and caffe centers do not run priced 1-on-1 lessons.
  const learningSubType = useAuthStore((s) => s.learningSubType);
  const showPrivateLessons = !learningSubType || learningSubType === 'teaching';
  const [showPrivateLesson, setShowPrivateLesson] = useState(false);
  const [privateRefresh, setPrivateRefresh] = useState(0);

  const refreshIntakes = () => {
    if (!showSportHero) return;
    api.get('/client-intakes')
      .then((r) => setIntakes(Array.isArray(r) ? r : r?.data || []))
      .catch(() => {});
  };
  useEffect(() => {
    refreshIntakes();
    if (!showSportHero) return undefined;
    // Clients sign on their own phones: pick up new signatures (and intakes
    // started from the mobile FAB) whenever the manager comes back to the tab.
    const onFocus = () => { if (document.visibilityState !== 'hidden') refreshIntakes(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [showSportHero]); // eslint-disable-line react-hooks/exhaustive-deps

  // The dashboard only needs recent + upcoming lessons (today's list, next 10,
  // and recent attendance trend). Cap the fetch so a center with thousands of
  // historical lessons doesn't ship its entire history to the dashboard.
  const DASHBOARD_LESSON_LIMIT = 500;
  const refreshLessons = async () => {
    const r = await api.get(`/lessons?limit=${DASHBOARD_LESSON_LIMIT}`);
    const v = Array.isArray(r) ? r : r?.data || [];
    setLessons(v);
  };

  const handleEditLesson = (lesson) => {
    setEditingLesson(lesson);
    setShowLessonModal(true);
  };

  const handleDeleteLesson = async (lesson) => {
    const ok = await confirm({
      title: t('common.delete'),
      message: t('lessons.confirmDelete'),
      confirmText: t('common.delete'),
      isDangerous: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/lessons/${lesson.id}`);
      success(t('lessons.deletedSuccess', { defaultValue: 'Lesson deleted' }));
      await refreshLessons();
    } catch (err) {
      console.error('Error deleting lesson:', err);
      error(t('lessons.deleteError', { defaultValue: 'Failed to delete lesson' }));
    }
  };

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setIsLoading(true);
      setLoadError(null);
      try {
        const [studentsRes, groupsRes, lessonsRes, teachersRes, activitiesRes, surveysRes, paymentsRes, registrationRes] =
          await Promise.allSettled([
            // Full roster (default page is 20) so KPI counts + insights reflect
            // the whole center, not just the first page.
            api.get('/students?limit=100000&enrich=0'),
            api.get('/groups'),
            api.get(`/lessons?limit=${DASHBOARD_LESSON_LIMIT}`),
            // /teachers is admin/manager-only; /teachers/assignable returns a
            // minimal roster for the lesson-assignment picker and is open to
            // any authenticated role (including teacher).
            api.get('/teachers/assignable').catch(() => []),
            api.get('/audit-logs/recent?limit=50').catch(() => []),
            api.get('/surveys').catch(() => []),
            api.get('/payments?status=overdue&limit=50').catch(() => []),
            // Registration campaigns are an admin/manager surface — a teacher's
            // request would only 403, so their dashboard doesn't ask.
            isTeacherView ? Promise.resolve([]) : courseRegistrationApi.listLinkGroupSummaries().catch(() => []),
          ]);
        if (cancelled) return;
        const unwrap = (r) => {
          if (r.status !== 'fulfilled') return [];
          const v = r.value;
          return Array.isArray(v) ? v : v?.data || [];
        };
        setStudents(unwrap(studentsRes));
        setGroups(unwrap(groupsRes));
        setLessons(unwrap(lessonsRes));
        setTeachers(unwrap(teachersRes));
        setActivities(unwrap(activitiesRes));
        setSurveys(unwrap(surveysRes));
        setPayments(unwrap(paymentsRes));
        setRegistrationGroups(unwrap(registrationRes));
        setLastUpdated(new Date());
      } catch (e) {
        if (!cancelled) setLoadError(e.message);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [effectiveTeacherFilter, isTeacherView]);

  // Scope lessons to the viewing teacher (backend enforces, this hides flash)
  const scopedLessons = useMemo(() => {
    if (!effectiveTeacherFilter) return lessons;
    return lessons.filter((l) => l.teacherId === effectiveTeacherFilter || (l.teacherIds || []).includes(effectiveTeacherFilter));
  }, [lessons, effectiveTeacherFilter]);

  const todayKey = new Date().toISOString().slice(0, 10);
  const todayLessons = useMemo(
    () =>
      scopedLessons.filter((l) => {
        const d = toDate(l.scheduledDate);
        return d && d.toISOString().slice(0, 10) === todayKey;
      }),
    [scopedLessons, todayKey],
  );

  const upcomingLessons = useMemo(() => {
    const todayStart = new Date(todayKey);
    return scopedLessons
      .map((l) => ({ l, d: toDate(l.scheduledDate) }))
      .filter(({ d }) => d && d >= todayStart)
      .sort((a, b) => a.d - b.d)
      .slice(0, 10)
      .map(({ l }) => l);
  }, [scopedLessons, todayKey]);

  const activeSurveys = useMemo(
    () => (Array.isArray(surveys) ? surveys : []).filter((s) => s.isActive !== false),
    [surveys],
  );

  const insights = useDashboardInsights({
    students,
    lessons: scopedLessons,
    activities,
    surveys,
    payments,
  });

  const sparklineValues = useMemo(
    () => (insights.attendanceSeries || []).map((w) => Math.round((w.rate || 0) * 100)),
    [insights.attendanceSeries],
  );

  // Learning centers (sport first): the student KPI counts only the kids who
  // actually come — inactive students (status 'inactive'; missing = active)
  // show as "+M inactive".
  const kpiStudents = useMemo(
    () => (isLearning ? students.filter((s) => s.status !== 'inactive') : students),
    [isLearning, students],
  );
  const inactiveCount = students.length - kpiStudents.length;

  const enrollmentSparkline = useMemo(() => {
    // Cumulative student count over the last 6 weeks
    const now = new Date();
    return Array.from({ length: 6 }, (_, idx) => {
      const cutoff = new Date(now);
      cutoff.setDate(cutoff.getDate() - (5 - idx) * 7);
      return kpiStudents.filter((s) => {
        const d = toDate(s.createdAt);
        return d && d <= cutoff;
      }).length || kpiStudents.length;
    });
  }, [kpiStudents]);

  const formatDelta = (delta) => {
    if (delta == null) return null;
    const sign = delta >= 0 ? '+' : '−';
    const value = formatPercent(Math.abs(delta), i18n.language);
    return {
      direction: delta > 0 ? 'up' : delta < 0 ? 'down' : 'flat',
      label: `${sign}${value}`,
    };
  };

  const attendanceDelta = formatDelta(insights.weeklyAttendanceDelta);
  const enrollmentDelta = (() => {
    const diff = insights.newThisWeek - insights.newLastWeek;
    if (!Number.isFinite(diff) || diff === 0) return null;
    return {
      direction: diff > 0 ? 'up' : 'down',
      label: `${diff > 0 ? '+' : '−'}${Math.abs(diff)}`,
    };
  })();

  const kpis = [
    {
      icon: Users,
      label: t('learningDashboard.kpi.students', { defaultValue: 'Students' }),
      value: inactiveCount > 0 ? (
        <>
          {kpiStudents.length}
          <span
            data-testid="kpi-students-inactive"
            style={{
              fontSize: '0.75rem', fontWeight: 700, marginInlineStart: '0.5rem', padding: '0.125rem 0.5rem',
              borderRadius: 999, verticalAlign: 'middle', whiteSpace: 'nowrap',
              backgroundColor: 'var(--warning-100, #fef3c7)', color: 'var(--warning-700, #b45309)',
            }}
          >
            {t('sport.clientStatus.kpiInactive', { count: inactiveCount })}
          </span>
        </>
      ) : kpiStudents.length,
      sparkline: enrollmentSparkline,
      delta: enrollmentDelta,
      onClick: perms.canViewAllStudents ? () => navigate('/students') : undefined,
      testId: 'kpi-students',
    },
    {
      icon: GraduationCap,
      label: t('learningDashboard.kpi.groups', { defaultValue: 'Groups' }),
      value: groups.length,
      onClick: () => navigate('/groups'),
      testId: 'kpi-groups',
    },
    {
      icon: BookOpen,
      label: t('learningDashboard.kpi.todayLessons', { defaultValue: "Today's lessons" }),
      value: todayLessons.length,
      testId: 'kpi-today-lessons',
    },
    {
      icon: Activity,
      label: t('learningDashboard.kpi.attendanceRate', { defaultValue: 'Attendance rate' }),
      value:
        insights.currentAttendanceRate != null
          ? formatPercent(insights.currentAttendanceRate, i18n.language)
          : '—',
      sparkline: sparklineValues,
      delta: attendanceDelta,
      testId: 'kpi-attendance',
    },
  ];

  if (isLoading) {
    return (
      <main className="learning-dashboard" data-testid="learning-dashboard-loading">
        <p>{t('common.loading', { defaultValue: 'Loading…' })}</p>
      </main>
    );
  }

  if (loadError) {
    return (
      <main className="learning-dashboard" data-testid="learning-dashboard-error" role="alert">
        <p style={{ color: 'var(--danger-600, #dc2626)' }}>{loadError}</p>
      </main>
    );
  }

  const isEmpty = students.length === 0 && groups.length === 0 && lessons.length === 0;

  return (
    <main id="main-content" className="learning-dashboard">
      <header className="learning-dashboard__header">
        <h1 className="learning-dashboard__title" data-testid="learning-dashboard-title">
          {isTeacherView
            ? t('learningDashboard.teacherTitle', { defaultValue: 'My classes' })
            : t('learningDashboard.title', { defaultValue: 'Learning dashboard' })}
        </h1>
        {lastUpdated && (
          <span className="learning-dashboard__meta" data-testid="learning-dashboard-updated">
            {t('learningDashboard.lastUpdated', {
              time: formatTime(lastUpdated, i18n.language),
              defaultValue: `Updated ${formatTime(lastUpdated, i18n.language)}`,
            })}
          </span>
        )}
      </header>

      {/* Sport: this week's trainings, always on top (Sun–Sat, prev / next). */}
      {showSportHero && (
        <SportWeekCard refreshKey={sportRefresh} onScheduleWeek={(wk) => setSportWeekOpen(wk || true)} />
      )}

      {showSportHero && (
        <div className="sport-hero-wrap">
        <section className="sport-hero" data-testid="sport-hero" data-count={canMoveStudents ? 4 : 3}>
          <button type="button" className="sport-hero__btn sport-hero__btn--intake" onClick={() => setShowIntake(true)} data-testid="sport-hero-intake">
            <span className="sport-hero__icon"><UserPlus size={26} /></span>
            <span className="sport-hero__text">
              <strong>{t('sport.dashboard.intake')}</strong>
              <small>{t('sport.dashboard.intakeDesc')}</small>
            </span>
          </button>
          <button type="button" className="sport-hero__btn sport-hero__btn--schedule" onClick={() => setShowSportSchedule(true)} data-testid="sport-hero-schedule">
            <span className="sport-hero__icon"><CalendarPlus size={26} /></span>
            <span className="sport-hero__text">
              <strong>{t('sport.dashboard.schedule')}</strong>
              <small>{t('sport.dashboard.scheduleDesc')}</small>
            </span>
          </button>
          <button type="button" className="sport-hero__btn sport-hero__btn--billing" onClick={() => setShowSportBilling(true)} data-testid="sport-hero-billing">
            <span className="sport-hero__icon"><Wallet size={26} /></span>
            <span className="sport-hero__text">
              <strong>{t('sport.dashboard.billing')}</strong>
              <small>{t('sport.dashboard.billingDesc')}</small>
            </span>
          </button>
          {canMoveStudents && (
            <button type="button" className="sport-hero__btn sport-hero__btn--move" onClick={() => setShowMoveFlow(true)} data-testid="sport-hero-move">
              <span className="sport-hero__icon"><ArrowLeftRight size={26} /></span>
              <span className="sport-hero__text">
                <strong>{t('groupMove.flow.action', { defaultValue: 'Move students between groups' })}</strong>
                <small>{t('groupMove.flow.actionDesc', { defaultValue: "Change a student's group — history and payments stay" })}</small>
              </span>
            </button>
          )}
        </section>
        </div>
      )}

      {showSportHero && <BirthdaysTodayChip />}
      {showSportHero && <SportBalancesCard refreshKey={sportRefresh} />}
      {showSportHero && <PendingIntakesCard intakes={intakes} onChanged={refreshIntakes} />}
      {showSportHero && <SportSessionsCard refreshKey={sportRefresh} />}

      <QuickActionsBar
        centerType="learning"
        role={perms.role}
        surveys={activeSurveys}
        onAddStudent={perms.canCreateStudent ? () => setShowQuickAddStudent(true) : undefined}
        onAddLesson={perms.canCreateLesson ? () => (showSportHero ? setShowSportSchedule(true) : setShowLessonModal(true)) : undefined}
        onAddGroup={perms.canCreateGroup ? () => navigate('/groups?add=1') : undefined}
        onRecordPayment={perms.canRecordPayment ? () => setShowQuickPayment(true) : undefined}
        onSearchStudent={() => setShowStudentSearch(true)}
        onAddSurvey={perms.canAssignSurvey ? () => navigate('/surveys') : undefined}
        onRecordMemorization={isQuran ? () => setShowRecord(true) : null}
        onAssignHomework={isQuran ? () => setShowAssignHw(true) : null}
        onCollectGroups={isQuran && !isTeacherView ? () => setShowCollect(true) : null}
        todayLessons={todayLessons}
        onAddPrivateLesson={showPrivateLessons && perms.canCreateLesson ? () => setShowPrivateLesson(true) : null}
        onMoveStudents={canMoveStudents && !showSportHero ? () => setShowMoveFlow(true) : null}
      />

      {/* Regular learning centers: who owes / is overdue → the payments board. */}
      {isLearning && !isSport && !isTeacherView && perms.canRecordPayment && <SportBalancesCard kind="learning" />}

      {showPrivateLessons && (
        <PrivateLessonsCard
          refreshKey={privateRefresh}
          onAdd={perms.canCreateLesson ? () => setShowPrivateLesson(true) : undefined}
          isManager={!isTeacherView && perms.canRecordPayment}
        />
      )}

      <section className="learning-dashboard__kpi-grid" data-testid="learning-kpi-row">
        {kpis.map((k) => (
          <KPICard
            key={k.label}
            icon={k.icon}
            label={k.label}
            value={k.value}
            sparkline={k.sparkline}
            delta={k.delta}
            onClick={k.onClick}
            testId={k.testId}
          />
        ))}
      </section>

      {/* Above the empty state on purpose: a center whose first campaign is
          still collecting registrations has no students yet, and that is
          exactly when this card is the most useful thing on the page. */}
      {registrationGroups.length > 0 && (
        <section className="learning-dashboard__registration" data-testid="learning-registration-summary">
          <RegistrationSummaryCard groups={registrationGroups} />
        </section>
      )}

      {isEmpty ? (
        <div className="learning-dashboard__empty" data-testid="learning-dashboard-empty">
          {t('learningDashboard.empty', {
            defaultValue:
              'No students, groups, or lessons yet. Start by adding a student or creating a group.',
          })}
        </div>
      ) : (
        <>
          <section className="learning-dashboard__primary-grid" data-testid="learning-primary-grid">
            <TodayTimeline
              lessons={todayLessons}
              centerType="learning"
              onEventClick={(event) =>
                navigate(`/calendar?lessonId=${encodeURIComponent(event.id || '')}`)
              }
            />
            <DashboardInsights insights={insights} />
          </section>

          <section className="learning-dashboard__charts" data-testid="learning-charts">
            <DashboardCharts
              lessons={scopedLessons}
              students={students}
              payments={payments}
              attendance={insights.attendanceSeries}
              isTherapy={false}
            />
          </section>

          <section className="learning-dashboard__secondary-grid">
            <div>
              <h2 className="learning-dashboard__section-title">
                {t('learningDashboard.upcomingLessons', {
                  defaultValue: 'Upcoming lessons',
                })}
              </h2>
              <UpcomingLessonsList
                lessons={upcomingLessons}
                onEdit={handleEditLesson}
                onDelete={handleDeleteLesson}
                isLoading={false}
              />
            </div>
            <aside>
              <h2 className="learning-dashboard__section-title">
                {t('learningDashboard.recentActivity', {
                  defaultValue: 'Recent activity',
                })}
              </h2>
              <RecentActivityList activities={activities} isLoading={false} />
            </aside>
          </section>
        </>
      )}

      <Suspense fallback={null}>
      {showPrivateLesson && (
        <PrivateLessonModal
          isOpen={showPrivateLesson}
          onClose={() => setShowPrivateLesson(false)}
          onCreated={() => { setPrivateRefresh((n) => n + 1); refreshLessons().catch(() => {}); }}
          students={students}
          teachers={teachers}
          isManager={!isTeacherView && perms.canRecordPayment}
          currentTeacherId={isTeacherView ? effectiveTeacherFilter : null}
        />
      )}

      {showLessonModal && (
        <LessonModal
          isOpen={showLessonModal}
          onClose={() => {
            setShowLessonModal(false);
            setEditingLesson(null);
          }}
          onSave={async (lessonData) => {
            if (editingLesson) {
              await api.put(`/lessons/${editingLesson.id}`, lessonData);
            } else if (lessonData.groupId && lessonData.groupId !== 'individual') {
              await api.post(`/lessons?groupId=${lessonData.groupId}`, lessonData);
            } else {
              await api.post('/lessons', lessonData);
            }
            await refreshLessons();
          }}
          groups={groups}
          students={students}
          teachers={teachers}
          lessons={lessons}
          user={user}
          editingLesson={editingLesson}
        />
      )}

      {showStudentSearch && (
        <StudentSearchModal
          isOpen={showStudentSearch}
          onClose={() => setShowStudentSearch(false)}
          students={students}
        />
      )}

      {isQuran && showRecord && (
        <RecordMemorizationModal
          isOpen={showRecord}
          onClose={() => setShowRecord(false)}
          students={students}
        />
      )}

      {isQuran && showAssignHw && (
        <AssignHomeworkModal
          isOpen={showAssignHw}
          onClose={() => setShowAssignHw(false)}
          students={students}
        />
      )}

      {isQuran && showCollect && (
        <CollectGroupsModal
          isOpen={showCollect}
          onClose={() => setShowCollect(false)}
        />
      )}

      {showSportHero && (
        <>
          {showIntake && <ClientIntakeModal
            isOpen={showIntake}
            onClose={() => setShowIntake(false)}
            onCreated={refreshIntakes}
          />}
          {showSportSchedule && <SportScheduleModal
            isOpen={showSportSchedule}
            onClose={() => setShowSportSchedule(false)}
            onScheduled={() => { refreshLessons().catch(() => {}); setSportRefresh((n) => n + 1); }}
            onGroupCreated={(g) => setGroups((prev) => [...prev, g])}
            groups={groups}
            students={students}
            teachers={teachers}
          />}
          {sportWeekOpen && <SportWeekScheduleModal
            isOpen={!!sportWeekOpen}
            initialWeekKey={typeof sportWeekOpen === 'string' ? sportWeekOpen : null}
            onClose={() => setSportWeekOpen(null)}
            onScheduled={() => { refreshLessons().catch(() => {}); setSportRefresh((n) => n + 1); }}
            groups={groups}
            teachers={teachers}
          />}
          {showSportBilling && <SportClientBillingSheet
            isOpen={showSportBilling}
            onClose={() => { setShowSportBilling(false); setSportRefresh((n) => n + 1); }}
            students={students}
          />}
        </>
      )}

      {showMoveFlow && (
        <MoveStudentsFlow
          isOpen
          onClose={() => setShowMoveFlow(false)}
          students={students}
          onMoved={() => { refreshLessons().catch(() => {}); setSportRefresh((n) => n + 1); }}
        />
      )}

      <QuickAddStudentModal
        isOpen={showQuickAddStudent}
        onClose={() => setShowQuickAddStudent(false)}
        onAdded={() => {
          // The student count KPI is read straight off this list.
          api.get('/students?limit=100000&enrich=0')
            .then((r) => setStudents(Array.isArray(r) ? r : r?.data || []))
            .catch(() => {});
        }}
      />

      {showQuickPayment && (
        <QuickPaymentModal
          isOpen
          onClose={() => setShowQuickPayment(false)}
          onRecorded={() => {
            // Overdue payments feed the insights panel, so a settled request
            // should stop being flagged without a manual refresh.
            api.get('/payments?status=overdue&limit=50')
              .then((r) => setPayments(Array.isArray(r) ? r : r?.data || []))
              .catch(() => {});
          }}
        />
      )}
      </Suspense>
    </main>
  );
}
